import { useState, useMemo, useEffect } from "react";
import { SelectionFeature } from "src/js/stores/map/store";
import { OutputAreaSummaryComparison, Loading } from "./OutputAreaDetailView/OutputAreaDetailView";
import { useDemographicsData } from "./hooks/useDemographicsData";
import { useSiteReportDemographicsContext } from "./DemographicsProvider";
import { useSiteReportConfigurationState } from "react-migration/layouts/SiteReport/useSiteReportConfiguration/useSiteReportConfiguration";
import { DEMOGRAPHICS_SECTION_IDS, US_DEMOGRAPHICS_SECTION_IDS } from "react-migration/constants";
import { SingleDesignation } from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";

interface SiteReportDemographicsWrapperProps {
  readonly selection: SelectionFeature;
  readonly printable?: boolean;
  readonly initialComparableId?: string;
}

export function SiteReportDemographicsWrapper({
  selection,
  printable = false,
  initialComparableId,
}: SiteReportDemographicsWrapperProps) {
  const [selectedDesignationId, setSelectedDesignationId] = useState<string | undefined>();
  const [availableGranularDesignations, setAvailableGranularDesignations] = useState<SingleDesignation[]>([]);

  const contextData = useSiteReportDemographicsContext();

  const demographicSectionIds = useMemo(() => {
    return contextData.region === "US" ? US_DEMOGRAPHICS_SECTION_IDS : DEMOGRAPHICS_SECTION_IDS;
  }, [contextData.region]);

  const checkboxState = useSiteReportConfigurationState("checkboxPaths", selection.id ?? "", demographicSectionIds);

  const hiddenSections = useMemo(() => {
    return demographicSectionIds.filter((sectionId: string) => {
      const path = `compsAndLocal.demographics.${sectionId}`;
      return !checkboxState.getCheckboxState(path);
    });
  }, [checkboxState, demographicSectionIds]);

  const selectedDesignationData = useDemographicsData({
    designationId: selectedDesignationId,
    useIntersections: true,
  });

  const {
    designation: outputArea,
    superiorDesignations: comparableDesignations,
    region,
    attributeComparisonSpec,
    loading,
  } = selectedDesignationId ? selectedDesignationData : contextData;

  useEffect(() => {
    if (!selectedDesignationId) {
      if (availableGranularDesignations.length > 0) {
        setSelectedDesignationId(availableGranularDesignations[0].id);
      } else if (contextData.targetDesignation) {
        setSelectedDesignationId(contextData.targetDesignation.id);
      }
    }
  }, [selectedDesignationId, availableGranularDesignations, contextData.targetDesignation]);

  if (loading || !outputArea) {
    return <Loading />;
  }

  if (!attributeComparisonSpec || !region) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <OutputAreaSummaryComparison
        region={region}
        outputArea={outputArea}
        superiorOutputAreas={comparableDesignations}
        attributeComparisonSummarySpec={attributeComparisonSpec}
        printable={printable}
        hiddenSections={hiddenSections}
        initialComparableId={initialComparableId}
      />
    </div>
  );
}
